import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import * as fa from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { type CSSProperties, type FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ContextMenuProps {
  options: {
    icon: IconDefinition;
    label: string;
    onClick: () => void;
    danger?: boolean;
  }[];
}

const optionHeight = 44; // h-11
const emptyHeight = 64;
const margin = 8;

export const ContextMenu: FC<ContextMenuProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);

  // The menu is rendered in a portal with fixed positioning, so it is not clipped by scrolling or overflow-hidden
  // ancestors (e.g. a table). That also means it doesn't follow its button, so it closes on scroll and resize.
  useEffect(() => {
    if (!open) {
      return;
    }
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const toggle = () => {
    if (open || !buttonRef.current) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const menuHeight = Math.max(props.options.length * optionHeight, emptyHeight);
    const spaceBelow = viewportHeight - rect.bottom;
    const opensUpwards = spaceBelow < menuHeight + margin && rect.top > spaceBelow;
    setMenuStyle({
      right: viewportWidth - rect.right,
      ...(opensUpwards ? { bottom: viewportHeight - rect.top } : { top: rect.bottom }),
    });
    setOpen(true);
  };

  return (
    <div className={"relative"}>
      <button
        ref={buttonRef}
        className={`h-8 w-8 border border-slate-200 rounded-sm text-slate-500 ${open ? "bg-slate-100 hover:bg-slate-200" : "bg-white hover:bg-slate-50"} text-xs`}
        onClick={toggle}
      >
        <FontAwesomeIcon icon={fa.faEllipsisVertical} />
      </button>

      {open &&
        createPortal(
          <>
            {/* Backdrop */}
            <div className={"fixed inset-0 bg-transparent z-30"} onClick={() => setOpen(false)} />

            {/* Menu */}
            <div
              className={"fixed z-40 w-64 bg-white shadow-md rounded-sm border border-slate-200"}
              style={menuStyle}
            >
              {props.options.map((option, i) => {
                return (
                  <button
                    key={i}
                    className={"hover:bg-slate-50 w-full flex h-11 items-center px-3"}
                    onClick={() => {
                      option.onClick();
                      setOpen(false);
                    }}
                  >
                    <div className={"w-6 flex items-center justify-center"}>
                      <FontAwesomeIcon
                        icon={option.icon}
                        className={`text-xs ${option.danger ? "text-red-600" : "text-brand-800"}`}
                      />
                    </div>
                    <span
                      className={`ml-2 font-medium ${option.danger ? "text-red-700" : "text-slate-900"}`}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })}
              {props.options.length === 0 && (
                <div className={"m-4 text-slate-500 text-center"}>No options available</div>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
